import * as yup from 'yup';
import { phoneRegExp } from '../../../utils/validation';

export interface AuthFormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  passwordConfirm: string;
  textOrEmailPreference: string;
  stateBuildLocation: string;
  citiesBuildLocation: string;
  totalBudget: string;
  needHome: string;
  needLand: string;
  needHomeLoan: string;
  annualGrossIncome?: string;
  creditScore?: string;
  isSecondaryResidence?: string;
  homeStylePreference?: string;
  homeStylePreferenceOtherDescription?: string;
  freeFormAbout?: string;
}

export type FirstStepSignUpFormValues = Pick<
  AuthFormValues,
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phone'
  | 'password'
  | 'passwordConfirm'
  | 'textOrEmailPreference'
>;

export const authValidationSchema = yup.object({
  firstName: yup.string().required('First Name'),
  lastName: yup.string().required('Last Name'),
  email: yup.string().email('Enter a valid email').required('Email'),
  password: yup
    .string()
    .min(8, 'Password should be of minimum 8 characters length')
    .required('Password'),
  passwordConfirm: yup
    .string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('Re-Enter Password'),
  phone: yup
    .string()
    .matches(phoneRegExp, 'Phone number is not valid')
    .required('Phone'),
  textOrEmailPreference: yup.string().required('Contact preference'),
});

export const secondPageSignUpValidationSchema = yup.object({
  stateBuildLocation: yup.string().required('Build Location Required - state'),
  citiesBuildLocation: yup.string().required('Build Location Required - city'),
  totalBudget: yup.string().required('Total budget'),
  needHome: yup.string().required('What are you looking for?'),
  needLand: yup.string(),
  needHomeLoan: yup.string().required('Need Financing?'),
  annualGrossIncome: yup.string(),
  creditScore: yup.string(),
  isSecondaryResidence: yup.string(),
  homeStylePreference: yup.string(),
  homeStylePreferenceOtherDescription: yup.string(),
});

export const authInitialValues: AuthFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  passwordConfirm: '',
  phone: '',
  textOrEmailPreference: '',
  stateBuildLocation: '',
  citiesBuildLocation: '',
  totalBudget: '',
  needHome: 'false',
  needLand: 'false',
  needHomeLoan: 'false',
  annualGrossIncome: '',
  creditScore: '',
  isSecondaryResidence: 'false',
  homeStylePreference: '',
  homeStylePreferenceOtherDescription: '',
  freeFormAbout: '',
};

export interface LoginFormValues {
  email: string;
  password: string;
}

export const loginInitialValues: LoginFormValues = {
  email: '',
  password: '',
};

export const loginValidationSchema = yup.object({
  email: yup.string().email('Enter a valid email').required('Email'),
  password: yup
    .string()
    .min(8, 'Password should be of minimum 8 characters length')
    .required('Password'),
});

export interface ResetPasswordFormValues {
  username: string;
}

export const resetPasswordInitialValues: ResetPasswordFormValues = {
  username: '',
};

export const resetPasswordSchema = yup.object({
  username: yup.string().email('Enter a valid email').required('Email'),
});

export interface UpdatePasswordFormValues {
  newPassword: string;
  confirmNewPassword: string;
  verificationCode: string;
}

export const updatePasswordInitialValues: UpdatePasswordFormValues = {
  newPassword: '',
  confirmNewPassword: '',
  verificationCode: '',
};

const specialCharacterRegex = /[-!@#$%^&*(),.?":{}|<>]/g;
const numericCharacterRegex = /[0-9]/;

export const updatePasswordSchema = yup.object({
  newPassword: yup
    .string()
    .min(8, 'New password should be of minimum 8 characters length')
    .matches(
      specialCharacterRegex,
      'Password must contain at least one special character'
    )
    .matches(
      numericCharacterRegex,
      'Password must contain at least one numeric character'
    )
    .required('Password is required'),

  confirmNewPassword: yup
    .string()
    .required('Please confirm your new password')
    .oneOf([yup.ref('newPassword')], "Passwords don't match."),

  verificationCode: yup.string().required('Please enter confirmation code'),
});
